import {useQuery} from "@tanstack/react-query";
import {apiEndpoint, useUser} from "@/components/Providers/JWTProvider.tsx";
import type {
    STATUS_CLOSED_NO_WAITLIST,
    STATUS_OPEN,
    STATUS_PARTIAL,
    STATUS_REGISTERED,
    STATUS_REGISTERED_WITH_CREDITS,
    STATUS_REGISTERED_WITH_CREDITS_PENDING_PAYMENT,
    STATUS_WAITLIST,
    STATUS_WAITLISTED,
} from "@/components/Providers/ProgramsProvider.tsx";
import {queryClient} from "@/utils/queryClient.ts";

export type SectionStatus = {
    id: string;
    paidAmount: number;
    paidOrPendingAmount: number;
    courseCredits: number | null;
    timeout?: string;
    studentOverrideCost?: number;
    status:
        | typeof STATUS_OPEN
        | typeof STATUS_WAITLISTED
        | typeof STATUS_REGISTERED
        | typeof STATUS_WAITLIST
        | typeof STATUS_PARTIAL
        | typeof STATUS_REGISTERED_WITH_CREDITS
        | typeof STATUS_REGISTERED_WITH_CREDITS_PENDING_PAYMENT
        | typeof STATUS_CLOSED_NO_WAITLIST;
};

export type SectionStatusMap = Map<string, SectionStatus>;

export const useProgramStatus = (programIds: string[] | undefined) => {
    const user = useUser();

    return useQuery({
        initialDataUpdatedAt: 0, //tell it the data is stale
        staleTime: 5 * 60 * 1000,
        initialData: new Map<string, SectionStatus>(),
        queryKey: ["programList", "status", user.user?.id, ...(programIds ?? [])],
        queryFn: async (): Promise<SectionStatusMap | null> => {
            if (!(user.user && programIds) || programIds.length === 0) {
                return new Map<string, SectionStatus>();
            }

            const url = new URL("/v1/program/status", apiEndpoint);
            const response = await user.user.apiFetch(url.toString(), {
                method: "POST",
                body: JSON.stringify({
                    programIds,
                }),
                signal: AbortSignal.timeout(10000), //10 second timeout
            });

            const csArray = (await response.json()) as SectionStatus[];
            return new Map<string, SectionStatus>(csArray.map(s => [s.id, s]));
        },
    });
};

export const useEnrolledProgramStatus = () => {
    const user = useUser();

    return useQuery({
        initialDataUpdatedAt: 0, //tell it the data is stale
        staleTime: 5 * 60 * 1000,
        initialData: new Map<string, SectionStatus>(),
        queryKey: ["programList", "status", user.user?.id, "myEnrolled"],
        queryFn: async (): Promise<SectionStatusMap | null> => {
            if (!user.user) {
                return new Map<string, SectionStatus>();
            }

            const url = new URL("/v1/program/enrolled", apiEndpoint);
            const response = await user.user.apiFetch(url.toString(), {
                signal: AbortSignal.timeout(10000), //10 second timeout
            });

            const csArray = (await response.json()) as SectionStatus[];
            return new Map<string, SectionStatus>(csArray.map(s => [s.id, s]));
        },
    });
};

export const clearProgramStatusCache = async (userId: string) => {
    await queryClient.invalidateQueries({
        queryKey: ["programList", "status", userId],
    });
};
